// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import {
    FormFieldClickable,
    FormFieldComponentProps,
    FormFieldIcon,
    FormFieldPresentationProps,
    FormFieldSizeVariant,
} from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import AnimatedTextField from "@insite/mobius/TextField/AnimatedTextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import { MobiusStyledComponentPropsWithRef } from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FormField from "@stoneage/MobiusOverrides/FormField/FormField";
import { sizeVariantValues } from "@stoneage/MobiusOverrides/FormField/formStyles";
import * as React from "react";
import styled, { ThemeConsumer } from "styled-components";

export interface TextFieldPresentationProps extends FormFieldPresentationProps<TextFieldComponentProps> {
    /** Props to be passed into the Clickable component that will optionally wrap the icon.
     * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
    iconClickableProps?: ClickablePresentationProps;
    /** The props for icon displaying on the text input field.
     * @themable */
    iconProps?: IconPresentationProps;
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<TextFieldComponentProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** Background color of the form element */
    backgroundColor?: string;
    /** The props for the textfield label */
    labelProps?: TypographyPresentationProps;
    /** Boolean value for width animation */
    isAnimated?: boolean;
}

type TextFieldComponentProps = MobiusStyledComponentPropsWithRef<
    "input",
    { ref?: React.Ref<HTMLInputElement | null> },
    {
        /** The props for icon displaying on the text input field. */
        iconProps?: IconPresentationProps;
        /** Props to be passed into the Clickable component that will optionally wrap the icon.
         * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
        iconClickableProps?: ClickableProps;
        /** Visually hidden text to be rendered within the text field clickable. */
        clickableText?: string;
        /** Error message to be displayed below the input. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the input. */
        hint?: React.ReactNode;
        /** Label to be displayed above the input. */
        label?: React.ReactNode;
        /** XNG-Changes: add clear icon props */
        clearClickableProps?: {
            clickableProps?: ClickableProps;
            iconProps?: IconPresentationProps;
        };
    } & Partial<FormFieldComponentProps>
>;

export type TextFieldProps = TextFieldComponentProps & TextFieldPresentationProps;

// XNG-Changes
export const InputStyle = styled.input<InjectableCss>`
    &&&,
    &&&:focus {
        padding-right: ${({ $dynamicPaddingRight }: any) => `${$dynamicPaddingRight ?? 12}px`};
    }
    ${injectCss}
`;

const omitKeys = [
    "sizeVariant",
    "border",
    "label",
    "labelPosition",
    "theme",
    "cssOverrides",
    "iconProps",
    "backgroundColor",
    "labelProps",
    "dispatch",
    "labelId",
    "isAnimated",
    "aria-expanded",
    "aria-controls",
] as (keyof Omit<
    TextFieldProps,
    | "id"
    | "type"
    | "onKeyDown"
    | "onChange"
    | "clickableText"
    | "disabled"
    | "error"
    | "hint"
    | "iconClickableProps"
    | "id"
    | "placeholder"
    | "required"
    | "disable"
    | "mergeCss"
    | "min"
    | "max"
    | "css"
    | "isAnimated"
    // XNG-Change
    | "clearClickableProps"
>)[];

const validForNumber = ({ altKey, ctrlKey, key }: React.KeyboardEvent<HTMLInputElement>) => {
    return altKey || ctrlKey || key.length > 1 || /[0-9\.\,\-\+]/.test(key);
};

const validNumber = (event: React.ChangeEvent<HTMLInputElement>, min?: number | string, max?: number | string) => {
    const number = Number(event.currentTarget.value);
    if (min !== undefined && number < Number(min)) {
        return false;
    }

    if (max !== undefined && number > Number(max)) {
        return false;
    }

    return true;
};

/**
 * TextField is a form element with an optional label, hint text, error message and optional icon.
 * Props not contained in the list below get passed into the input component (e.g. event handlers, `value`, etc).
 */
const TextField: React.FC<TextFieldProps & HasDisablerContext> = props => {
    return (
        <ThemeConsumer>
            {(theme?: BaseTheme) => {
                const {
                    type,
                    onKeyDown,
                    onChange,
                    clickableText,
                    disable,
                    disabled,
                    error,
                    hint,
                    iconClickableProps,
                    id,
                    placeholder,
                    required,
                    mergeCss,
                    min,
                    max,
                    isAnimated,
                    // XNG-Change
                    clearClickableProps,
                    ...otherProps
                } = props;

                const isDisabled = !!(disable || disabled);
                const inputId = id || uniqueId();
                const labelId = `${inputId}-label`;
                const inputLabelObj = otherProps.label === 0 || otherProps.label ? { "aria-labelledby": labelId } : {};
                const descriptionId = `${inputId}-description`;
                const hasDescription = error || hint;

                const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                    { theme, ...otherProps },
                    { component: "textField", category: "formField" },
                );
                const labelProps = applyProp("labelProps");
                const sizeVariant: FormFieldSizeVariant = applyProp("sizeVariant", "default");
                const iconProps = spreadProps("iconProps");
                const resolvedMergeCss = mergeCss ?? theme?.textField?.defaultProps?.mergeCss;

                const internalOnKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (type === "number" && !validForNumber(event)) {
                        event.preventDefault();
                    }

                    onKeyDown?.(event);
                };

                const internalOnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
                    if (type === "number" && !validNumber(event, min, max)) {
                        event.preventDefault();
                        return;
                    }

                    onChange?.(event);
                };

                const formFieldProps = {
                    ...iconProps,
                    size: sizeVariantValues[sizeVariant].icon,
                    // XNG-Change: change color to match design
                    color: isDisabled ? "text.disabled" : iconProps.color || "common.ui7",
                };

                // <XNG-Changes>
                const clearIconProps = {
                    ...clearClickableProps?.iconProps,
                    color: isDisabled ? "common.disabled" : clearClickableProps?.iconProps?.color || "common.ui7",
                    src: clearClickableProps?.iconProps?.src || X,
                    width: clearClickableProps?.iconProps?.size || 14,
                    height: clearClickableProps?.iconProps?.size || 14,
                };

                const clearIconElement = clearClickableProps ? (
                    <FormFieldClickable {...clearClickableProps.clickableProps} disabled={isDisabled}>
                        <FormFieldIcon {...clearIconProps} />
                        <VisuallyHidden>{translate("Clear")}</VisuallyHidden>
                    </FormFieldClickable>
                ) : null;

                // </XNG-Changes>

                const formIcon = iconProps ? (
                    iconClickableProps ? (
                        <FormFieldClickable {...iconClickableProps} disabled={isDisabled}>
                            <FormFieldIcon {...formFieldProps} />
                            <VisuallyHidden>{clickableText}</VisuallyHidden>
                        </FormFieldClickable>
                    ) : (
                        <FormFieldIcon {...formFieldProps} />
                    )
                ) : null;

                const InputElement = !isAnimated ? (
                    <>
                        <InputStyle
                            {...{ disabled: isDisabled, placeholder, required }}
                            {...inputLabelObj}
                            {...omitMultiple(otherProps, omitKeys)}
                            ref={props.ref}
                            min={min}
                            max={max}
                            type={type || "text"}
                            id={inputId}
                            onKeyDown={internalOnKeyDownHandler}
                            onChange={internalOnChangeHandler}
                            aria-labelledby={labelId}
                            aria-describedby={hasDescription ? descriptionId : undefined}
                            aria-invalid={!!error}
                            aria-required={!disabled && required}
                            tabIndex={0}
                            css={applyStyledProp("css", resolvedMergeCss)}
                            // XNG-Change
                            {...{ $dynamicPaddingRight: 12 + (iconProps?.src ? 20 : 0) + (clearIconElement ? 20 : 0) }} // we will use attr css to set padding
                        />
                        {/* <XNG-Changes>: add clear icon element */}
                        <StyledWrapper className="clickable-wrapper">
                            {clearIconElement && otherProps.value && clearIconElement}
                            {!!clearIconElement && otherProps.value && !!formIcon && <div className="divider" />}
                            {formIcon}
                        </StyledWrapper>
                        {/* </XNG-Changes> */}
                    </>
                ) : (
                    <AnimatedTextField
                        {...{ disabled: isDisabled, placeholder, required }}
                        {...inputLabelObj}
                        {...omitMultiple(otherProps, omitKeys)}
                        min={min}
                        max={max}
                        type={type || "text"}
                        id={inputId}
                        onKeyDown={internalOnKeyDownHandler}
                        onChange={internalOnChangeHandler}
                        aria-labelledby={labelId}
                        aria-describedby={hasDescription ? descriptionId : undefined}
                        aria-invalid={!!error}
                        aria-required={!disabled && required}
                        tabIndex={0}
                        css={applyStyledProp("css", resolvedMergeCss)}
                    />
                );

                return (
                    <FormField
                        {...props}
                        labelProps={labelProps}
                        descriptionId={descriptionId}
                        formInput={InputElement}
                        labelId={labelId}
                        inputId={inputId}
                    />
                );
            }}
        </ThemeConsumer>
    );
};

TextField.displayName = "TextField";
/** @component */
export default withDisabler(TextField);
